import axiosInstance from '../utils/axios';

/**
 * 사용자 관리 서비스 모듈
 * 
 * 새로운 MVC 아키텍처의 User API와 통신하는 서비스 레이어
 */
class UserService {
    constructor() {
        this.baseURL = '/user';
    }

    /**
     * 사용자 목록 조회
     * @param {number} page - 페이지 번호 (1부터 시작)
     * @param {number} pageSize - 페이지 크기
     * @param {number} searchKind - 검색 종류 (0: 전체, 1: 이름, 2: 아이디, 3: 이메일)
     * @param {string} keyword - 검색 키워드
     * @param {number} state - 사용자 상태 (1: 정상, 2: 탈퇴)
     * @returns {Promise<Object>} 사용자 목록 응답
     */
    async getUserList(page = 1, pageSize = 10, searchKind = 0, keyword = '', state = 1) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/list`, {
                params: {
                    page,
                    pageSize,
                    searchKind,
                    keyword,
                    state
                }
            });
            return response.data;
        } catch (error) {
            console.error('getUserList error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 사용자 상세 정보 조회
     * @param {number} userIdx - 사용자 ID
     * @returns {Promise<Object>} 사용자 상세 정보
     */
    async getUserDetail(userIdx) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/${userIdx}`);
            return response.data;
        } catch (error) {
            console.error('getUserDetail error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 사용자 이름 조회
     * @param {number} userIdx - 사용자 ID
     * @returns {Promise<Object>} 사용자 이름
     */
    async getUserName(userIdx) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/${userIdx}/name`);
            return response.data;
        } catch (error) {
            console.error('getUserName error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 검색 자동완성을 위한 사용자 정보 조회
     * @param {number} searchKind - 검색 종류 (0: 전체, 1: 이름, 2: 아이디, 3: 이메일)
     * @param {string} keyword - 검색 키워드
     * @param {number} userState - 사용자 상태 (1: 정상, 2: 탈퇴)
     * @param {number} limit - 결과 개수 제한
     * @returns {Promise<Object>} 검색 결과
     */
    async getSearchSuggest(searchKind = 0, keyword = '', userState = 1, limit = 10) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/search-suggest`, {
                params: {
                    searchKind,
                    keyword,
                    state: userState,
                    limit
                }
            });
            return response.data;
        } catch (error) {
            console.error('getSearchSuggest error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 사용자 탈퇴 처리
     * @param {number} userIdx - 사용자 ID
     * @returns {Promise<Object>} 탈퇴 처리 결과
     */
    async signoutUser(userIdx) {
        try {
            const response = await axiosInstance.post(`${this.baseURL}/${userIdx}/signout`);
            return response.data;
        } catch (error) {
            console.error('signoutUser error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 사용자 엑셀 데이터 조회
     * @param {number} searchKind - 검색 종류
     * @param {string} keyword - 검색 키워드
     * @param {number} state - 사용자 상태
     * @returns {Promise<Object>} 엑셀 데이터
     */
    async getUserExcelData(searchKind = 0, keyword = '', state = 1) {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/excel`, {
                params: {
                    searchKind,
                    keyword,
                    state
                }
            });
            return response.data;
        } catch (error) {
            console.error('getUserExcelData error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 정책 동의 현황 목록 조회
     * @param {number} page - 페이지 번호
     * @param {number} pageSize - 페이지 크기
     * @param {number} searchKind - 검색 종류
     * @param {string} keyword - 검색 키워드
     * @returns {Promise<Object>} 정책 동의 현황
     */
    async getPolicyAgreeList(page = 1, pageSize = 10, searchKind = 0, keyword = '') {
        try {
            const response = await axiosInstance.get(`${this.baseURL}/policy-agree`, {
                params: {
                    page,
                    pageSize,
                    searchKind,
                    keyword
                }
            });
            return response.data;
        } catch (error) {
            console.error('getPolicyAgreeList error:', error);
            throw this.handleError(error);
        }
    }

    /**
     * 에러 처리 헬퍼 메서드
     * @param {Error} error - 에러 객체
     * @returns {Error} 처리된 에러
     */
    handleError(error) {
        if (error.response) {
            // 서버 응답이 있는 경우
            const { status, data } = error.response;
            const message = data?.message || `HTTP ${status} 에러가 발생했습니다.`;
            const customError = new Error(message);
            customError.status = status;
            customError.code = data?.code;
            customError.errorCode = data?.errorCode;
            return customError;
        } else if (error.request) {
            // 요청은 보냈지만 응답이 없는 경우
            return new Error('서버에 연결할 수 없습니다. 네트워크를 확인해주세요.');
        } else {
            // 기타 에러
            return new Error('요청 처리 중 오류가 발생했습니다.');
        }
    }
}

// 싱글톤 인스턴스 생성 및 내보내기
const userService = new UserService();
export default userService;
